import posthog from 'posthog-js'
import React, {useState, useEffect} from 'react'
import {useXpertKnowledgeTutorialContext, XpertKnowledgeTutorialContextProvider} from './XpertKnowledgeTutorialContext'
import {Block, Col, Row} from 'jsxstyle'
import StarRating from '../../components/StarRating/StarRating'
import {useXpertKnowledgeContext} from '../XpertKnowledge/XpertKnowledgeContext'
import {ReactComponent as CalendarIcon} from '../../common/assets/calendar-icon.svg'
import {ReactComponent as UserIcon} from '../../common/assets/user-icon.svg'
import {ReactComponent as LeftArrowIcon} from '../../common/assets/left-arrow-icon.svg'
import {formatDate} from '../../common/utils/dateUtils'
import XpertKnowledgeTutorialSection from './components/XpertKnowledgeTutorialSection'
import XpertKnowledgeSidebar from './components/XpertKnowledgeTutorialSidebar'
import {Outlet, useNavigate} from 'react-router-dom'
import Loader from '../../components/Loader'
import i18n from '../../common/i18n'
import XpertKnowledgeTutorialRightBar from './components/XpertKnowledgeTutorialRightBar'
import XpertKnowledgeTutorialPublicBar from './components/XpertKnowledgeTutorialPublicBar'

const XpertKnowledgeTutorial: React.FC = () => {
  const {xpertKnowledgeSections, xpertKnowledgeTutorials, selectedXpertKnowledgeTutorial, submitRating, setSelectedXpertKnowledgeTutorial} =
    useXpertKnowledgeTutorialContext()
  const {selectedXpertKnowledge} = useXpertKnowledgeContext()
  const navigate = useNavigate()

  const firstSectionId =
    xpertKnowledgeSections.length > 0
      ? xpertKnowledgeSections.reduce((minSection, currentSection) => (currentSection.position < minSection.position ? currentSection : minSection))
          .id
      : undefined

  const [selectedSectionId, setSelectedSectionId] = useState<number | undefined>(firstSectionId)
  const [selectedSubSectionId, setSelectedSubSectionId] = useState<number | undefined>()

  useEffect(() => {
    const hash = window.location.hash
    if (hash) {
      const element = document.querySelector(hash)
      if (element) element.scrollIntoView({behavior: 'smooth', block: 'start'})
    }
  }, [selectedSectionId, selectedSubSectionId])

  if (!selectedXpertKnowledge || !selectedXpertKnowledgeTutorial) {
    return (
      <Col height="100%" width="100%" justifyContent="center" alignItems="center" gap="20px">
        <Block>
          <Loader size={75} />
        </Block>
        <Block fontWeight={500} lineHeight="60px" fontSize="34px">
          {i18n.t('xk.tutorial.loading')}
        </Block>
      </Col>
    )
  }

  return (
    <Col height="100%" width="100%" backgroundColor="#FFFFFF">
      {selectedXpertKnowledge.isPublic && <XpertKnowledgeTutorialPublicBar xpertKnowledgeId={selectedXpertKnowledge.id} />}
      <Row height="100%">
        <XpertKnowledgeSidebar
          selectedSectionId={selectedSectionId ?? firstSectionId}
          setSelectedSectionId={setSelectedSectionId}
          selectedSubSectionId={selectedSubSectionId}
          setSelectedSubSectionId={setSelectedSubSectionId}
          selectedXpertKnowledge={selectedXpertKnowledge}
          selectedXpertKnowledgeTutorial={selectedXpertKnowledgeTutorial}
          setSelectedXpertKnowledgeTutorial={setSelectedXpertKnowledgeTutorial}
          xpertKnowledgeTutorials={xpertKnowledgeTutorials}
        />
        <Col gap="12px" padding="26px 10px 34px 70px" width="100%" height="100%" overflowY="scroll">
          <Row justifyContent="space-between" alignItems="flex-start">
            <Col gap="10px" width="auto">
              <Row
                component="button"
                border="none"
                backgroundColor="transparent"
                padding="4px 8px 4px 3px"
                hoverBackgroundColor="#ebebed"
                gap="10px"
                cursor="pointer"
                borderRadius="4px"
                props={{
                  onClick: () => navigate('/xpert-knowledge'),
                }}
                alignItems="center"
                marginBottom="10px"
              >
                <LeftArrowIcon width={16} height={16} color="#909090" />
                <Block color="#909090" fontWeight={500} lineHeight="24px" fontSize="16px">
                  {i18n.t('xk.tutorial.back')}
                </Block>
              </Row>
              <Row justifyContent="space-between" alignItems="center">
                <Row gap="10px" alignItems="center">
                  <Block fontSize="14px" lineHeight="24px" fontWeight={400} color="#909090">
                    {i18n.t('xk.tutorial.rate')}
                  </Block>
                  <StarRating
                    rating={selectedXpertKnowledgeTutorial.userRating?.rating ?? 0}
                    editable={true}
                    onChange={(newRating) => {
                      submitRating(selectedXpertKnowledge.id, selectedXpertKnowledgeTutorial.id, newRating)
                      posthog.capture('rating_submited', {
                        rating_value: newRating,
                      })
                    }}
                  />
                </Row>
              </Row>
              {selectedXpertKnowledge.publishTutorial?.userRating?.rating && selectedXpertKnowledge.publishTutorial?.userRating?.rating < 4 && (
                <Row backgroundColor="#FFF0E3" borderRadius="8px" padding="16px" color="#AD5400" alignItems="center" gap="5px">
                  <Block>{i18n.t('xk.tutorial.feedbackLabel')}</Block>
                  <Block
                    textDecoration="underline"
                    color="#AD5400"
                    cursor="pointer"
                    props={{
                      onClick: () =>
                        window.open(
                          'https://forms.office.com/Pages/ResponsePage.aspx?id=esxqs5U0xEWTYPry8Iltf1dcy_r0yUtIgqDduZ9JjDBURTI0SkZMQUxWUUQ2NUVWTTVPWElVSU1WRy4u',
                          '_blank',
                          'noopener,noreferrer'
                        ),
                    }}
                  >
                    {i18n.t('xk.tutorial.feedbackLinkLabel')}
                  </Block>
                </Row>
              )}
            </Col>
          </Row>

          <Block fontSize="32px" fontWeight={500} lineHeight="48px" color="black" marginTop="10px">
            {selectedXpertKnowledgeTutorial.title}
          </Block>
          <Row gap="20px" marginBottom="10px">
            <Row alignItems="center" gap="5px">
              <UserIcon width={20} height={20} color="#909090" />
              <Block fontSize="12px" color="#66666" fontWeight={300} props={{'data-attr-masked': 'true'} as any}>
                {selectedXpertKnowledge.creator?.fullName}
              </Block>
            </Row>
            <Row alignItems="center" gap="5px">
              <CalendarIcon width={16} height={16} color="#909090" />
              <Block fontSize="12px" color="#66666" fontWeight={300}>
                {formatDate(selectedXpertKnowledge.createdAt)}
              </Block>
            </Row>
          </Row>
          {selectedXpertKnowledge.publishTutorial?.id !== selectedXpertKnowledgeTutorial.id && (
            <Block width="97%" backgroundColor="#FFF0E3" borderRadius="10px" padding="12px 20px" fontSize="14px" fontWeight={400}>
              {`You are viewing the version ${selectedXpertKnowledgeTutorial.version} of this document.`}
            </Block>
          )}
          {selectedXpertKnowledgeTutorial.audioBlob && (
            <Block width="50%">
              <audio
                controls
                style={{width: '100%'}}
                onPlay={() => {
                  posthog.capture('audio_played')
                }}
              >
                <source src={selectedXpertKnowledgeTutorial.audioBlob.url} type="audio/mpeg" />
                Your browser does not support the audio element.
              </audio>
            </Block>
          )}
          <Block fontWeight={400} fontSize="14px" lineHeight="22px" paddingBottom="16px" marginBottom="6px" borderBottom="1px solid #DADCE0">
            {selectedXpertKnowledgeTutorial.description}
          </Block>
          {selectedXpertKnowledgeTutorial.sections.map((section) => (
            <XpertKnowledgeTutorialSection key={section.id} xpertKnowledgeSection={section} />
          ))}
        </Col>
        <XpertKnowledgeTutorialRightBar />
      </Row>
      <Outlet />
    </Col>
  )
}

export default () => (
  <XpertKnowledgeTutorialContextProvider>
    <XpertKnowledgeTutorial />
  </XpertKnowledgeTutorialContextProvider>
)
